// src/pages/CotizacionesGuardadas.js
// Pipeline de cotizaciones — módulo semi-oculto accesible desde alertas CRM y Agenda
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "../supabaseClient";
import { generarPDF } from "../utils/generarPDF";
import Protegido from "../components/Protegido";
import "../estilos/CrearDocumentoEstilo.css";

// ─── Helpers ───────────────────────────────────────────────────
const soloFecha = (valor) => {
  if (!valor) return "—";
  const s = String(valor).trim().slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
};

const money = (n) => `$${Number(n || 0).toLocaleString("es-CO")}`;

const diasDesde = (fecha) => {
  if (!fecha) return 999;
  const diff = new Date() - new Date(String(fecha).slice(0, 10));
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
};

const badgeAntiguedad = (dias) => {
  if (dias <= 2) return { texto: "Nueva", bg: "#dcfce7", color: "#166534" };
  if (dias <= 5) return { texto: `${dias}d`, bg: "#fef9c3", color: "#854d0e" };
  if (dias <= 15) return { texto: `${dias}d — Fría`, bg: "#fee2e2", color: "#991b1b" };
  return { texto: `${dias}d — Muy fría`, bg: "#fecaca", color: "#7f1d1d" };
};

// ═══════════════════════════════════════════════════════════════
export default function CotizacionesGuardadas() {
  const navigate = useNavigate();
  const [cotizaciones, setCotizaciones] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("todas"); // todas | frias | recientes
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [ordenarPor, setOrdenarPor] = useState("antiguedad"); // antiguedad | fecha | monto

  // ─── Carga inicial ──────────────────────────────────────────
  useEffect(() => {
    const cargar = async () => {
      setCargando(true);
      const { data, error } = await supabase
        .from("cotizaciones")
        .select("*, clientes(*)")
        .order("id", { ascending: false })
        .limit(200);

      if (error) {
        console.error("Error cargando cotizaciones:", error);
      } else {
        setCotizaciones(data || []);
      }
      setCargando(false);
    };
    cargar();
  }, []);

  // ─── Filtros + Ordenamiento ──────────────────────────────────
  const cotizacionesFiltradas = useMemo(() => {
    let resultado = [...cotizaciones];

    // Filtro texto
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      resultado = resultado.filter(
        (c) =>
          (c.clientes?.nombre || "").toLowerCase().includes(q) ||
          (c.numero || "").toLowerCase().includes(q) ||
          (c.tipo_evento || "").toLowerCase().includes(q) ||
          (c.lugar_evento || "").toLowerCase().includes(q)
      );
    }

    // Filtro fecha
    if (fechaDesde) {
      resultado = resultado.filter((c) => {
        const f = (c.fecha_evento || c.fecha || "").slice(0, 10);
        return f >= fechaDesde;
      });
    }
    if (fechaHasta) {
      resultado = resultado.filter((c) => {
        const f = (c.fecha_evento || c.fecha || "").slice(0, 10);
        return f <= fechaHasta;
      });
    }

    // Filtro estado
    if (filtroEstado === "frias") {
      resultado = resultado.filter((c) => diasDesde(c.fecha) > 5);
    } else if (filtroEstado === "recientes") {
      resultado = resultado.filter((c) => diasDesde(c.fecha) <= 5);
    }

    // Ordenamiento
    if (ordenarPor === "antiguedad") {
      resultado.sort((a, b) => diasDesde(b.fecha) - diasDesde(a.fecha));
    } else if (ordenarPor === "fecha") {
      resultado.sort((a, b) => new Date(b.fecha_evento || 0) - new Date(a.fecha_evento || 0));
    } else if (ordenarPor === "monto") {
      resultado.sort((a, b) => Number(b.total_neto || 0) - Number(a.total_neto || 0));
    }

    return resultado;
  }, [cotizaciones, busqueda, filtroEstado, fechaDesde, fechaHasta, ordenarPor]);

  // ─── KPIs ────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const total = cotizacionesFiltradas.length;
    const valorTotal = cotizacionesFiltradas.reduce((s, c) => s + Number(c.total_neto || 0), 0);
    const frias = cotizacionesFiltradas.filter((c) => diasDesde(c.fecha) > 5).length;
    const diasPromedio = total > 0
      ? Math.round(cotizacionesFiltradas.reduce((s, c) => s + diasDesde(c.fecha), 0) / total)
      : 0;
    return { total, valorTotal, frias, diasPromedio };
  }, [cotizacionesFiltradas]);

  // ─── Acciones ────────────────────────────────────────────────
  const editarCotizacion = (cot) => {
    const cliente = cot.clientes || {};
    navigate("/crear-documento", {
      state: {
        documento: {
          ...cot,
          nombre_cliente: cliente.nombre || "",
          identificacion: cliente.identificacion || "",
          telefono: cliente.telefono || "",
          direccion: cliente.direccion || "",
          email: cliente.email || "",
          fecha_creacion: cot.fecha_creacion || cot.fecha || null,
          numero: cot.numero || "",
          esEdicion: true,
          idOriginal: cot.id,
        },
        tipo: "cotizacion",
      },
    });
  };

  const convertirAPedido = (cot) => {
    const cliente = cot.clientes || {};
    navigate("/crear-documento", {
      state: {
        documento: {
          ...cot,
          nombre_cliente: cliente.nombre || "",
          identificacion: cliente.identificacion || "",
          telefono: cliente.telefono || "",
          direccion: cliente.direccion || "",
          email: cliente.email || "",
          fecha_creacion: new Date().toISOString(),
          numero: "",
          esEdicion: false,
          idOriginal: null,
        },
        tipo: "orden",
      },
    });
  };

  const manejarPDF = async (cot) => {
    const cliente = cot.clientes || {};
    await generarPDF(
      {
        ...cot,
        nombre_cliente: cliente.nombre || "N/A",
        identificacion: cliente.identificacion || "N/A",
        telefono: cliente.telefono || "N/A",
        direccion: cliente.direccion || "N/A",
        email: cliente.email || "N/A",
        fecha_creacion: cot.fecha_creacion || cot.fecha || null,
        fecha_evento: cot.fecha_evento || null,
      },
      "cotizacion"
    );
  };

  const abrirWhatsApp = (cot) => {
    const cliente = cot.clientes || {};
    const tel = (cliente.telefono || "").replace(/\D/g, "");
    if (!tel) return;
    const nombre = cliente.nombre || "cliente";
    const total = money(cot.total_neto);
    const msg = `Hola ${nombre}, te escribo respecto a la cotización ${cot.numero || ""} por ${total}. ¿Pudiste revisarla? Quedo atento a tus comentarios.`;
    window.open(`https://wa.me/57${tel}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  // ─── Limpiar filtros ─────────────────────────────────────────
  const limpiarFiltros = () => {
    setBusqueda("");
    setFiltroEstado("todas");
    setFechaDesde("");
    setFechaHasta("");
  };

  const hayFiltros = busqueda || filtroEstado !== "todas" || fechaDesde || fechaHasta;

  // ─── Render ──────────────────────────────────────────────────
  return (
    <Protegido>
      <div className="sw-pagina">
        <div className="sw-pagina-contenido" style={{ maxWidth: 960 }}>

          {/* HEADER */}
          <div className="sw-header">
            <h1 className="sw-header-titulo">📋 Seguimiento de Cotizaciones</h1>
            <p style={{ fontSize: 13, color: "#6b7280", margin: "4px 0 0" }}>
              Revisa, edita y convierte tus cotizaciones en pedidos
            </p>
          </div>

          {/* KPIs */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 16 }}>
            {[
              { label: "Cotizaciones", valor: kpis.total, color: "#0077B6", bg: "#f0f9ff", tip: "Total de cotizaciones en el sistema según los filtros aplicados" },
              { label: "Valor total", valor: money(kpis.valorTotal), color: "#059669", bg: "#ecfdf5", tip: "Suma del valor de todas las cotizaciones visibles" },
              { label: "Frías (>5 días)", valor: kpis.frias, color: kpis.frias > 0 ? "#dc2626" : "#6b7280", bg: kpis.frias > 0 ? "#fef2f2" : "#f9fafb", tip: "Cotizaciones creadas hace más de 5 días sin convertir a pedido. Requieren seguimiento" },
              { label: "Antigüedad prom.", valor: `${kpis.diasPromedio}d`, color: "#7c3aed", bg: "#f5f3ff", tip: "Promedio de días desde que se crearon las cotizaciones. Si es alto, conviene hacer seguimiento" },
            ].map((kpi, i) => (
              <div key={i} title={kpi.tip} style={{
                padding: "14px 16px",
                borderRadius: 10,
                background: kpi.bg,
                border: "1px solid #e5e7eb",
                textAlign: "center",
                cursor: "help",
                transition: "transform 0.15s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "none"}
              >
                <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 4, textTransform: "uppercase" }}>{kpi.label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: kpi.color }}>{kpi.valor}</div>
              </div>
            ))}
          </div>

          {/* FILTROS */}
          <div className="cd-card" style={{ marginBottom: 16 }}>
            <div className="cd-card-body">
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                {/* Búsqueda */}
                <div style={{ flex: "1 1 200px" }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>🔍 Buscar</label>
                  <input
                    type="text"
                    placeholder="Cliente, número, evento..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    style={{ width: "100%", padding: "9px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, boxSizing: "border-box" }}
                  />
                </div>

                {/* Estado */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Estado</label>
                  <select
                    value={filtroEstado}
                    onChange={(e) => setFiltroEstado(e.target.value)}
                    style={{ padding: "9px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, cursor: "pointer" }}
                  >
                    <option value="todas">Todas</option>
                    <option value="frias">🥶 Solo frías (&gt;5 días)</option>
                    <option value="recientes">🟢 Solo recientes</option>
                  </select>
                </div>

                {/* Fecha desde */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Desde</label>
                  <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)}
                    style={{ padding: "9px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, cursor: "pointer" }} />
                </div>

                {/* Fecha hasta */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Hasta</label>
                  <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)}
                    style={{ padding: "9px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, cursor: "pointer" }} />
                </div>

                {/* Ordenar */}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 }}>Ordenar</label>
                  <select
                    value={ordenarPor}
                    onChange={(e) => setOrdenarPor(e.target.value)}
                    style={{ padding: "9px 12px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, cursor: "pointer" }}
                  >
                    <option value="antiguedad">Más antiguas primero</option>
                    <option value="fecha">Fecha evento</option>
                    <option value="monto">Mayor monto</option>
                  </select>
                </div>

                {hayFiltros && (
                  <button onClick={limpiarFiltros} className="cd-btn cd-btn-gris" style={{ fontSize: 12 }}>
                    ✕ Limpiar
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* CONTADOR */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 14px",
            background: "#f9fafb",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            marginBottom: 12,
            fontSize: 13,
            color: "#374151",
            fontWeight: 500,
          }}>
            <span>{cotizacionesFiltradas.length} cotización{cotizacionesFiltradas.length !== 1 ? "es" : ""}</span>
            {hayFiltros && <span style={{ color: "#9ca3af" }}>filtrado de {cotizaciones.length} totales</span>}
          </div>

          {/* ESTADO VACÍO */}
          {!cargando && cotizacionesFiltradas.length === 0 && (
            <div style={{
              textAlign: "center",
              padding: "48px 24px",
              color: "#9ca3af",
              background: "white",
              borderRadius: 12,
              border: "1px solid #e5e7eb",
            }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
              <div style={{ fontSize: 15, fontWeight: 500 }}>
                {cotizaciones.length === 0 ? "No hay cotizaciones registradas aún" : "No hay cotizaciones con estos filtros"}
              </div>
              <div style={{ fontSize: 13, marginTop: 6 }}>
                {cotizaciones.length === 0
                  ? "Crea tu primera cotización desde el módulo de documentos"
                  : "Ajusta los filtros para ver más resultados"}
              </div>
            </div>
          )}

          {/* LISTA DE COTIZACIONES */}
          {cotizacionesFiltradas.map((cot) => {
            const cliente = cot.clientes || {};
            const dias = diasDesde(cot.fecha);
            const badge = badgeAntiguedad(dias);
            const total = Number(cot.total_neto || 0);
            const tieneWhatsApp = !!(cliente.telefono || "").replace(/\D/g, "");

            return (
              <div
                key={cot.id}
                style={{
                  background: "white",
                  borderRadius: 12,
                  border: `1px solid ${dias > 5 ? "#fecaca" : "#e5e7eb"}`,
                  marginBottom: 10,
                  overflow: "hidden",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                }}
              >
                {/* Header */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "10px 16px",
                  background: dias > 5 ? "linear-gradient(135deg, #fef2f2, #fff1f2)" : "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
                  borderBottom: `1px solid ${dias > 5 ? "#fecaca" : "#bae6fd"}`,
                  flexWrap: "wrap",
                  gap: 8,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
                    <span style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#0077B6",
                      background: "rgba(0,119,182,0.1)",
                      padding: "2px 8px",
                      borderRadius: 6,
                    }}>
                      {cot.numero || "COT-???"}
                    </span>
                    <span style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>
                      {cliente.nombre || "Sin cliente"}
                    </span>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {/* Badge antigüedad */}
                    <span style={{
                      fontSize: 11,
                      fontWeight: 600,
                      padding: "3px 10px",
                      borderRadius: 20,
                      background: badge.bg,
                      color: badge.color,
                    }}>
                      {badge.texto}
                    </span>

                    {/* Monto */}
                    <span style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: "#059669",
                    }}>
                      {money(total)}
                    </span>
                  </div>
                </div>

                {/* Body */}
                <div style={{ padding: "10px 16px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "6px 16px", marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 500 }}>Fecha evento</div>
                      <div style={{ fontSize: 13, color: "#111827" }}>📅 {soloFecha(cot.fecha_evento)}</div>
                    </div>
                    {cot.tipo_evento && (
                      <div>
                        <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 500 }}>Tipo evento</div>
                        <div style={{ fontSize: 13, color: "#111827" }}>🎉 {cot.tipo_evento}</div>
                      </div>
                    )}
                    {cot.lugar_evento && (
                      <div>
                        <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 500 }}>Lugar</div>
                        <div style={{ fontSize: 13, color: "#111827" }}>
                          📍{" "}
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cot.lugar_evento)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#0077B6", textDecoration: "none" }}
                          >
                            {cot.lugar_evento}
                          </a>
                        </div>
                      </div>
                    )}
                    {cliente.telefono && (
                      <div>
                        <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 500 }}>Teléfono</div>
                        <div style={{ fontSize: 13, color: "#111827" }}>
                          <a href={`tel:${cliente.telefono}`} style={{ color: "#0077B6", textDecoration: "none" }}>
                            📞 {cliente.telefono}
                          </a>
                        </div>
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 500 }}>Creada</div>
                      <div style={{ fontSize: 13, color: "#111827" }}>🕐 {soloFecha(cot.fecha)}</div>
                    </div>
                  </div>

                  {/* Productos preview */}
                  {Array.isArray(cot.productos) && cot.productos.length > 0 && (
                    <div style={{
                      fontSize: 12,
                      color: "#6b7280",
                      background: "#f9fafb",
                      padding: "6px 10px",
                      borderRadius: 6,
                      marginBottom: 10,
                    }}>
                      {cot.productos.slice(0, 3).map((p, i) => (
                        <span key={i}>
                          {i > 0 && " · "}
                          {p.cantidad}x {p.nombre}
                        </span>
                      ))}
                      {cot.productos.length > 3 && <span style={{ color: "#9ca3af" }}> y {cot.productos.length - 3} más</span>}
                    </div>
                  )}

                  {/* Botones de acción */}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <button
                      onClick={() => editarCotizacion(cot)}
                      title="Editar cotización"
                      style={{
                        padding: "7px 12px",
                        background: "rgba(0,180,216,0.08)",
                        border: "1px solid rgba(0,180,216,0.25)",
                        borderRadius: 7,
                        cursor: "pointer",
                        fontSize: 13,
                        color: "#0077B6",
                        fontWeight: 500,
                      }}
                    >
                      ✏️ Editar
                    </button>
                    <button
                      onClick={() => manejarPDF(cot)}
                      title="Descargar PDF"
                      style={{
                        padding: "7px 12px",
                        background: "rgba(100,116,139,0.08)",
                        border: "1px solid rgba(100,116,139,0.2)",
                        borderRadius: 7,
                        cursor: "pointer",
                        fontSize: 13,
                        color: "#374151",
                        fontWeight: 500,
                      }}
                    >
                      📄 PDF
                    </button>
                    {tieneWhatsApp && (
                      <button
                        onClick={() => abrirWhatsApp(cot)}
                        title="Seguimiento por WhatsApp"
                        style={{
                          padding: "7px 12px",
                          background: "rgba(37,211,102,0.08)",
                          border: "1px solid rgba(37,211,102,0.25)",
                          borderRadius: 7,
                          cursor: "pointer",
                          fontSize: 13,
                          color: "#166534",
                          fontWeight: 500,
                        }}
                      >
                        💬 WhatsApp
                      </button>
                    )}
                    <button
                      onClick={() => convertirAPedido(cot)}
                      title="Convertir a pedido"
                      style={{
                        padding: "7px 12px",
                        background: "rgba(16,185,129,0.1)",
                        border: "1px solid rgba(16,185,129,0.3)",
                        borderRadius: 7,
                        cursor: "pointer",
                        fontSize: 13,
                        color: "#065f46",
                        fontWeight: 600,
                        marginLeft: "auto",
                      }}
                    >
                      🔄 Convertir a pedido
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {cargando && (
            <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
              ⏳ Cargando cotizaciones...
            </div>
          )}

        </div>
      </div>
    </Protegido>
  );
}